import { useCallback, useId } from 'react';

import { Checkbox } from '@/lib/base/forms/checkbox';
import { Input } from '@/lib/base/forms/input/input';
import {
  RadioButton,
  RadioGroup,
} from '@/lib/base/radio-buttons/radio-buttons';
import type {
  ClarifyingAnswer,
  ClarifyingQuestion as ClarifyingQuestionData,
  ClarifyingQuestionOption,
} from '@/lib/proposals/core/types';

import { ClarifyingRichText } from './ClarifyingRichText';

// ── Module-scope sub-component ─────────────────────────────────────────────────
// Avoids inline arrow functions binding optionId (stable references, Rule 5)
// and satisfies react/no-unstable-nested-components.

interface CheckboxOptionProps {
  option: ClarifyingQuestionOption;
  checked: boolean;
  disabled: boolean;
  onToggle: (optionId: string) => void;
  orgId?: number;
}

function ClarifyingCheckboxOption({
  option,
  checked,
  disabled,
  onToggle,
  orgId,
}: CheckboxOptionProps) {
  const handleChange = useCallback(() => {
    onToggle(option.id);
  }, [onToggle, option.id]);

  return (
    <Checkbox
      label={<ClarifyingRichText content={option.label} orgId={orgId} />}
      description={
        option.description ? (
          <ClarifyingRichText content={option.description} orgId={orgId} />
        ) : undefined
      }
      isSelected={checked}
      isDisabled={disabled}
      onChange={handleChange}
    />
  );
}
ClarifyingCheckboxOption.displayName = 'ClarifyingCheckboxOption';

// ── Types ──────────────────────────────────────────────────────────────────────

interface Props {
  question: ClarifyingQuestionData;
  answer: ClarifyingAnswer | undefined;
  onSelectOption: (optionId: string) => void;
  onToggleOption: (optionId: string) => void;
  onSetOther: (text: string) => void;
  disabled: boolean;
  orgId?: number;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function ClarifyingQuestion({
  question,
  answer,
  onSelectOption,
  onToggleOption,
  onSetOther,
  disabled,
  orgId,
}: Props) {
  const legendId = useId();

  const selectedOptionId =
    answer?.kind === 'option' ? (answer.optionIds?.[0] ?? '') : '';
  const otherText = answer?.kind === 'other' ? (answer.text ?? '') : '';

  return (
    <fieldset
      className='flex flex-col gap-3 border-0 m-0 p-0'
      data-question-id={question.id}
    >
      <legend
        id={legendId}
        className='text-body-small font-semibold text-[var(--color-text-primary)]'
      >
        <ClarifyingRichText content={question.header} orgId={orgId} />
      </legend>

      {question.prompt && (
        <p className='text-body-small text-[var(--color-text-secondary)]'>
          <ClarifyingRichText content={question.prompt} orgId={orgId} />
        </p>
      )}

      {!question.multiSelect ? (
        <RadioGroup
          aria-labelledby={legendId}
          isDisabled={disabled}
          value={selectedOptionId}
          onChange={onSelectOption}
        >
          {question.options.map(option => (
            <RadioButton
              key={option.id}
              value={option.id}
              label={
                <ClarifyingRichText content={option.label} orgId={orgId} />
              }
              description={
                option.description ? (
                  <ClarifyingRichText
                    content={option.description}
                    orgId={orgId}
                  />
                ) : undefined
              }
            />
          ))}
        </RadioGroup>
      ) : (
        <div className='flex flex-col gap-3'>
          {question.options.map(option => (
            <ClarifyingCheckboxOption
              key={option.id}
              option={option}
              checked={
                answer?.kind === 'option' &&
                (answer.optionIds?.includes(option.id) ?? false)
              }
              disabled={disabled}
              onToggle={onToggleOption}
              orgId={orgId}
            />
          ))}
        </div>
      )}

      {question.allowOther && (
        <Input
          aria-label={`Other answer for ${question.header}`}
          value={otherText}
          onChange={onSetOther}
          isDisabled={disabled}
          placeholder='Describe your answer'
        />
      )}
    </fieldset>
  );
}
